import {
  ControlPrefix,
  DerivativeSettlementTypeEnum,
  Flex,
  FormControlSizes,
  HStack,
  HelpIcon,
  InputVariants,
  MixpanelEvent,
  RangeSelect,
  SearchSelect,
  getOptionCoin,
  useConstant,
  useDynamicCallback,
  useMarketAccountsContext,
  useMarketsContext,
  useMixpanel,
  type MarketAccount,
  type RangeSelectObject,
  type SecurityTypes,
} from '@talos/kyoko';
import { compareAsc } from 'date-fns';
import { concat, identity, range, uniqBy } from 'lodash-es';
import { memo, useCallback, useMemo } from 'react';
import { ALL_EXPIRIES_ITEM, STS_DIGITAL_MARKET_NAME } from '../types';
import { formatOptionExpiry } from '../utils';

import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import Big from 'big.js';
import { DisclaimerTooltip, cmeDisclaimer } from '../../../../../components/DisclaimerTooltip';
import { getOptionsAfterFilter } from '../hooks/useOptionChainData';
import { OptionColumnSelector } from './OptionColumnSelector';
import {
  updateATM,
  updateDeltaRange,
  updateExpiry,
  updateMarket,
  updateMarketAccount,
  updatePanelSymbol,
  updateSettlementCurrency,
  updateSettlementType,
  updateStrikeRange,
} from './filterSlice';
import { selectMeta, selectOptionFilter, selectStrikeFilter } from './selectors';
import {
  getSettlementCurrencyOptions,
  getSettlementTypeOptions,
  isMarketCME,
  showAllExpiriesOptionForMarket,
} from './utils';

const formatOptionFilterExpiry = (expiry: SecurityTypes['Option']['Expiration']) => formatOptionExpiry(expiry, '{Mon}');

const CURRENCY_SELECTOR_STYLE = { width: 100 };
const FIT_CONTENT_STYLE = { width: 'fit-content' };
const EXPIRY_SELECTOR_STYLE = { width: 160 };

export const OptionFilter = memo(function OptionFilter({ panelID }: { panelID: string }) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const optionFilter = useAppStateSelector(state => selectOptionFilter(state, panelID));
  const strikeFilter = useAppStateSelector(state => selectStrikeFilter(state, panelID));
  const meta = useAppStateSelector(selectMeta);
  const { expirationByMarketByCurrencyIdentity, optionSecurityBySymbol } = meta;

  const strikesAtExpiry = useMemo(
    () =>
      uniqBy(
        Array.from(optionSecurityBySymbol.values()).filter(o => getOptionCoin(o) === optionFilter.underlyingCode),

        o => o.StrikePrice
      )
        .map(o => o.StrikePrice)
        .toSorted((a, b) => Big(a).cmp(b)),
    [optionFilter.underlyingCode, optionSecurityBySymbol]
  );

  const markets = useMemo(
    () =>
      Array.from(
        new Set(
          Array.from(optionSecurityBySymbol.values())
            .filter(option => option.BaseCurrency === optionFilter.currency)
            .flatMap(option => option.Markets || [])
        )
      ),
    [optionSecurityBySymbol, optionFilter.currency]
  );

  const expiries = useMemo(() => {
    const items = optionFilter.underlyingCode
      ? concat(
          Array.from(
            expirationByMarketByCurrencyIdentity.get(optionFilter.underlyingCode)?.get(optionFilter.market) || []
          ).toSorted((a, b) => compareAsc(new Date(a), new Date(b)))
        )
      : [];
    if (showAllExpiriesOptionForMarket(optionFilter.market)) {
      items.unshift(ALL_EXPIRIES_ITEM);
    }
    return items;
  }, [expirationByMarketByCurrencyIdentity, optionFilter.underlyingCode, optionFilter.market]);

  const { marketDisplayNameByName } = useMarketsContext();

  const handleOnExpiryChange = useDynamicCallback((expiry?: string) => {
    if (expiry) {
      if (expiry === ALL_EXPIRIES_ITEM) {
        mixpanel.track(MixpanelEvent.ViewAllExpiries);
      } else {
        mixpanel.track(MixpanelEvent.ViewSingleExpiry);
      }
      mixpanel.track(MixpanelEvent.UpdateOptionFilter, { type: 'expiry', value: expiry });
      dispatch(updateExpiry({ expiry, panelID }));
    }
  });

  // Possible underlyingcodes given optionFilter
  const underlyingCodes: CoinSelectItem[] = useMemo(() => {
    const allOptions = Array.from(optionSecurityBySymbol.values());
    const availableOptions = getOptionsAfterFilter(allOptions, {
      market: optionFilter.market,
      expiry: optionFilter.expiry,
      // We're not interested in narrowing down based on code or currency
      underlyingCode: undefined,
      currency: undefined,
    });
    // Grab the possible pairs of underlying code and base currencies
    return uniqBy(availableOptions, option => getOptionCoin(option)).map(option => {
      const optionCoin = getOptionCoin(option);
      return { currency: option.BaseCurrency, underlyingCode: optionCoin };
    });
  }, [optionSecurityBySymbol, optionFilter.market, optionFilter.expiry]);

  const handleOnCoinChange = useDynamicCallback((coinSelection?: CoinSelectItem) => {
    if (coinSelection) {
      mixpanel.track(MixpanelEvent.UpdateOptionFilter, { type: 'currency' });
      const { currency, underlyingCode } = coinSelection;
      dispatch(updatePanelSymbol({ underlyingCode, currency, panelID }));
    }
  });
  const coinSelection: CoinSelectItem = useMemo(() => {
    return { currency: optionFilter.currency, underlyingCode: optionFilter.underlyingCode ?? optionFilter.currency };
  }, [optionFilter.currency, optionFilter.underlyingCode]);
  const coinLabelGetter = useDynamicCallback(
    (coinSelect: CoinSelectItem) => coinSelect.underlyingCode ?? coinSelect.currency
  );

  const getMarketLabel = useCallback((m: string) => marketDisplayNameByName.get(m) || m, [marketDisplayNameByName]);
  const handleOnMarketChange = useCallback(
    (market?: string) => {
      if (market) {
        mixpanel.track(MixpanelEvent.UpdateOptionFilter, { type: 'market' });
        dispatch(updateMarket({ market, panelID }));
      }
    },
    [dispatch, mixpanel, panelID]
  );
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();

  const marketAccounts = useMemo(
    () => (optionFilter.market && marketAccountsByMarket.get(optionFilter.market)) || [],
    [marketAccountsByMarket, optionFilter.market]
  );
  const getMarketAccountLabel = useCallback((marketAccount: MarketAccount) => marketAccount.DisplayName, []);
  const handleOnMarketAccountChange = useDynamicCallback((marketAccount: MarketAccount | undefined) => {
    mixpanel.track(MixpanelEvent.ChangeMarketAccount);
    dispatch(updateMarketAccount({ marketAccount, panelID }));
  });

  const getAtmFilterLabel = useCallback((value: number) => value.toString(), []);
  const atmOptions = useMemo(
    () => range(1, Math.max(Math.floor(strikesAtExpiry.length / 4), 2)),
    [strikesAtExpiry.length]
  );
  const handleOnATMChange = useDynamicCallback((atmRange?: number) => {
    mixpanel.track(MixpanelEvent.UpdateOptionStrike, { type: 'atm', value: atmRange });
    dispatch(updateATM({ atm: atmRange ?? null, panelID }));
  });

  const getStrikeFilterLabel = useCallback((value: string) => parseInt(value).toString(), []);
  const handleStrikeRangeChange = useDynamicCallback((range: RangeSelectObject | undefined) => {
    mixpanel.track(MixpanelEvent.UpdateOptionStrike, { type: 'strike' });
    dispatch(updateStrikeRange({ strikeRange: range, panelID }));
  });

  /** Create a list of [0, 0.1,....1.0] for delta selection */
  const deltaRanges = useConstant(range(1, 11, 1).map(n => Big(n).div(10).toFixed()));
  const handleDeltaRangeChange = useDynamicCallback((range: RangeSelectObject | undefined) => {
    mixpanel.track(MixpanelEvent.UpdateOptionStrike, { type: 'delta', value: range });
    dispatch(updateDeltaRange({ deltaRange: range, panelID }));
  });

  // Settlement Type / Currency — only shown for markets that expose settlement variants (STS Digital).
  const showSettlementSelectors = optionFilter.market === STS_DIGITAL_MARKET_NAME;
  const settlementTypeOptions = useMemo(
    () =>
      showSettlementSelectors
        ? getSettlementTypeOptions(meta, optionFilter.currency, optionFilter.underlyingCode, optionFilter.market)
        : [],
    [showSettlementSelectors, meta, optionFilter.currency, optionFilter.underlyingCode, optionFilter.market]
  );
  const settlementCurrencyOptions = useMemo(
    () =>
      showSettlementSelectors
        ? getSettlementCurrencyOptions(
            meta,
            optionFilter.currency,
            optionFilter.underlyingCode,
            optionFilter.market,
            optionFilter.settlementType
          )
        : [],
    [
      showSettlementSelectors,
      meta,
      optionFilter.currency,
      optionFilter.underlyingCode,
      optionFilter.market,
      optionFilter.settlementType,
    ]
  );
  const isPhysicalSettlement = optionFilter.settlementType === DerivativeSettlementTypeEnum.Physical;
  const identitySettlementLabel = useCallback((value: string) => value, []);
  const handleSettlementTypeChange = useDynamicCallback((settlementType?: DerivativeSettlementTypeEnum) => {
    if (settlementType) {
      mixpanel.track(MixpanelEvent.UpdateOptionFilter, { type: 'settlementType', value: settlementType });
      dispatch(updateSettlementType({ settlementType, panelID }));
    }
  });
  const handleSettlementCurrencyChange = useDynamicCallback((settlementCurrency?: string) => {
    if (settlementCurrency) {
      mixpanel.track(MixpanelEvent.UpdateOptionFilter, { type: 'settlementCurrency', value: settlementCurrency });
      dispatch(updateSettlementCurrency({ settlementCurrency, panelID }));
    }
  });

  return (
    <Flex gap="spacing4" justifyContent="space-between" alignItems="center" w="100%" p="spacing8" px="spacing12">
      <HStack gap="inherit" justifyContent="space-between" w="100%" flexWrap="wrap">
        <Flex gap="inherit" alignItems="center">
          {markets.length > 0 && (
            <SearchSelect
              getLabel={getMarketLabel}
              onChange={handleOnMarketChange}
              options={markets}
              initialSortByLabel={false}
              selection={optionFilter.market || undefined}
              prefix={<ControlPrefix>Exchanges</ControlPrefix>}
              size={FormControlSizes.Small}
              style={FIT_CONTENT_STYLE}
              data-testid="options-market-selector"
            />
          )}

          <SearchSelect
            options={underlyingCodes}
            onChange={handleOnCoinChange}
            getLabel={coinLabelGetter}
            selection={coinSelection}
            size={FormControlSizes.Small}
            style={CURRENCY_SELECTOR_STYLE}
            data-testid="options-coin-selector"
          />

          {marketAccounts.length > 0 && (
            <SearchSelect
              options={marketAccounts}
              getLabel={getMarketAccountLabel}
              onChange={handleOnMarketAccountChange}
              selection={
                (optionFilter.marketAccountName && marketAccountsByName.get(optionFilter.marketAccountName)) ||
                undefined
              }
              size={FormControlSizes.Small}
              prefix={<ControlPrefix>Market Account</ControlPrefix>}
              gridTemplateColumns="max-content auto min-content"
              style={FIT_CONTENT_STYLE}
              data-testid="options-market-account-selector"
            />
          )}

          <SearchSelect
            onChange={handleOnExpiryChange}
            options={expiries}
            initialSortByLabel={false}
            selection={optionFilter.expiry || undefined}
            getLabel={formatOptionFilterExpiry}
            prefix={<ControlPrefix>Expiry</ControlPrefix>}
            suffix={
              !showAllExpiriesOptionForMarket(optionFilter.market) ? (
                <HelpIcon tooltip="You must select a specific expiry for the chosen Market." />
              ) : null
            }
            size={FormControlSizes.Small}
            style={EXPIRY_SELECTOR_STYLE}
            data-testid="options-expiry-selector"
          />

          {showSettlementSelectors && (
            <>
              <SearchSelect
                options={settlementTypeOptions}
                onChange={handleSettlementTypeChange}
                getLabel={identitySettlementLabel}
                selection={optionFilter.settlementType || undefined}
                initialSortByLabel={false}
                prefix={<ControlPrefix>Settlement</ControlPrefix>}
                size={FormControlSizes.Small}
                style={FIT_CONTENT_STYLE}
                data-testid="options-chain-settlement-type-selector"
              />
              <SearchSelect
                options={settlementCurrencyOptions}
                onChange={handleSettlementCurrencyChange}
                getLabel={identitySettlementLabel}
                selection={optionFilter.settlementCurrency || undefined}
                disabled={isPhysicalSettlement}
                initialSortByLabel={false}
                prefix={<ControlPrefix>Currency</ControlPrefix>}
                size={FormControlSizes.Small}
                style={FIT_CONTENT_STYLE}
                data-testid="options-chain-settlement-currency-selector"
              />
            </>
          )}
        </Flex>

        <HStack gap="inherit">
          {isMarketCME(optionFilter.market) && <DisclaimerTooltip children={cmeDisclaimer} />}

          <SearchSelect
            selection={strikeFilter.atm || undefined}
            getLabel={getAtmFilterLabel}
            initialSortByLabel={false}
            onChange={handleOnATMChange}
            options={atmOptions}
            placeholder={strikeFilter.atm ? 'Around ATM' : undefined}
            size={FormControlSizes.Small}
            prefix={<ControlPrefix>Around ATM{strikeFilter.atm ? ':' : ''}</ControlPrefix>}
            variant={strikeFilter.atm ? InputVariants.Primary : undefined}
            showClear
            data-testid="atm-filtering-selector"
          />
          <RangeSelect
            title="Strike range"
            options={strikesAtExpiry}
            getLabel={getStrikeFilterLabel}
            onChange={handleStrikeRangeChange}
            range={strikeFilter.strikePrice}
            data-testid="strike-filtering-selector"
          />

          <RangeSelect
            title="Delta range"
            options={deltaRanges}
            getLabel={identity}
            onChange={handleDeltaRangeChange}
            range={strikeFilter.delta}
            data-testid="delta-filtering-selector"
          />
        </HStack>
      </HStack>

      <OptionColumnSelector panelID={panelID} />
    </Flex>
  );
});
type CoinSelectItem = { currency: string; underlyingCode: string };
